import type { ComponentProps } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import clsx from 'clsx'
import { rem } from 'polished'
import moment from 'moment'
import Clickable from 'components/Common/Clickable'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { FormattedMessage, useIntl } from 'react-intl'

import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import TextLink from 'LuxKit/TextLink'
import Group from 'components/utils/Group'
import SolidUsdCircleIcon from 'LuxKit/Icons/solid/SolidUsdCircleIcon'
import LineFireIcon from 'LuxKit/Icons/line/LineFireIcon'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import { isLPCTacticalEnabled } from 'lib/offer/offerUtils'
import { getThemeClassName } from 'lib/theme/themeUtils'
import { isDateWithinAnyDiscountPeriod } from 'components/OfferPage/utils'
import { EmptyArray } from 'lib/array/arrayUtils'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import SolidCircleIcon from 'LuxKit/Icons/solid/SolidCircleIcon'
import type { OtherRoomsFromPrice } from 'selectors/offerPage/offerPageSelectors'
import { CalendarBurnContext } from '../CalendarBurnContext'
import { useCalendarRecommendationsView } from '../CalendarRecommendationsViewContext'
import { useCalendarBurnDiscountedPrice } from '../useCalendarBurnDiscountedPrice'
import { getFixedFlashCalendarDayPricingState, getFixedFlashCalendarSelectionMode } from '../fixedFlashLengthOfStayCalendarUtils'

const HeaderGroup = styled(Group)`
  position: relative;
`

const CellWrapper = styled(Clickable)`
  --transition-timing: 0.2s;

  position: relative;
  display: flex;
  flex-direction: column;
  padding: ${rem(4)} ${rem(8)};
  transition: background-color var(--transition-timing);
  min-height: ${rem(72)};
  border-block-end: thin solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-inline-end: thin solid ${props => props.theme.palette.neutral.surface.lightForeground};

  /* Anchor a content-box frame at the end edges so its borders extend outward. */
  &.border-block-start::after, &.border-inline-start::after {
    content: '';
    position: absolute;
    box-sizing: content-box;
    inline-size: 100%;
    block-size: 100%;
    inset-block-end: 0;
    inset-inline-end: 0;
    z-index: 1;
    pointer-events: none;
  }

  &.border-block-start::after {
    border-block-start: thin solid ${props => props.theme.palette.neutral.surface.lightForeground};
  }

  &.border-inline-start::after {
    border-inline-start: thin solid ${props => props.theme.palette.neutral.surface.lightForeground};
  }

  &:not(.check-in):not(.check-out):not(:disabled):not(.unselectable):not(.other-availability):not(.is-complementary) {
    ${mediaHoverable} {
      &:hover {
        background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
      }
    }
    &:focus {
      background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
    }
  }

  &:disabled, &.unselectable {
    background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
    cursor: not-allowed;
  }

  &.check-in, &.check-out {
    background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
  }

  &.between-day {
    background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
  }

  &.minimum-stay {
    background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
  }

  &.compact-prior-date {
    .full-day-information {
      visibility: hidden;
      pointer-events: none;
    }

    &:focus {
      .compact-prior-date-price {
        visibility: hidden;
      }

      .full-day-information {
        visibility: visible;
        pointer-events: auto;
      }
    }

    ${mediaHoverable} {
      &:hover {
        .compact-prior-date-price {
          visibility: hidden;
        }

        .full-day-information {
          visibility: visible;
          pointer-events: auto;
        }
      }
    }
  }

  &.is-bundle {
    justify-content: space-between;
  }

  &.sold-out-background {
    background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  }

  &.is-complementary {
    --polygon-width: ${rem(16)};
    --polygon-base: 0;
    background-color: ${props => props.theme.palette.brand.primary.normal};

     /* The Bundle & Save Polygon */
    &::before {
      content: '';
      position: absolute;
      inset-block-start: 0;
      inset-inline-start: 0;
      inline-size: 100%;
      block-size: 100%;
      z-index: 0;
      clip-path: polygon(0 0, calc(var(--polygon-base) + var(--polygon-width)) 0, var(--polygon-base) 100%, 0 100%);
      background-color: ${props => props.theme.palette.neutral.surface.darkBackground};
      transform-origin: center;
    }

    &.complementary-check-in {
      position: relative;
      background-color: ${props => props.theme.palette.neutral.surface.normalBackground};

      &::before {
        inset-inline-start: auto;
        inset-inline-end: 0;
        transform: scaleX(-1) scaleY(-1);
      }

      &.check-out {
        background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
      }
    }

    &.complementary-check-out {
      --polygon-base: 20%;
      position: relative;
      background-color: ${props => props.theme.palette.neutral.surface.normalBackground};

      &::before {
        transform: scaleX(1);
      }

      &.check-in {
        background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
      }
    }

    /* The polygon is physical, so mirror it to keep the wedge on the side of the day the stay runs from */
    [dir="rtl"] &::before,
    [dir="rtl"] &.complementary-check-out::before {
      transform: scaleX(-1);
    }

    [dir="rtl"] &.complementary-check-in::before {
      transform: scaleY(-1);
    }
  }
`

const PropertyNameContainer = styled.div`
  position: relative;
  z-index: 1;
  height: 100%;
`

const Main = styled.div`
  position: relative;
  opacity: 1;
  pointer-events: auto;
  transition: opacity 0.2s;
  display: flex;
  flex-direction: column;

  &.hidden {
    opacity: 0;
    pointer-events: none;
  }

  &.align-items-end {
    align-items: end;
  }
`

const PriorDatePriceSlot = styled.div`
  &.compact-prior-date {
    display: grid;

    > * {
      grid-area: 1 / 1;
    }
  }
`

const CompactPriorDatePrice = styled.div`
  display: flex;
  flex-direction: column;
`

const CompactPriorDatePricePlaceholder = styled.div`
  visibility: hidden;
  block-size: ${rem(16)};
`

const CompactPriorDatePriceRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  flex-wrap: nowrap;
`

const CompactPriorDatePriceValue = styled.span`
  margin-inline-start: 0.25em;
`

const BundleAndSaveAdditionalAmount = styled.div`
  text-align: end;
`

const CalendarDayTooltip = styled(Caption)`
  position: absolute;
  z-index: 2;
  inset-block-start: 0;
  inset-inline-start: 50%;
  transform: translate(-50%, calc(-100% - ${rem(6)}));
  padding: ${rem(6)} ${rem(8)};
  border-radius: ${rem(2)};
  color: ${props => props.theme.palette.neutral.surface.normalBackground};
  background-color: ${props => props.theme.palette.neutral.surface.darkBackground};
  box-shadow: ${props => props.theme.shadow.bottom.small};
  white-space: nowrap;
  pointer-events: none;

  [dir='rtl'] & {
    transform: translate(50%, calc(-100% - ${rem(6)}));
  }
`

type CalendarDayClickAction =
  'none' |
  'invalid-bundle-date' |
  'minimum-stay-disabled' |
  'maximum-stay-disabled' |
  'select-check-out' |
  'select-earlier-check-in' |
  'select-fixed-flash-check-in' |
  'switch-to-hotel' |
  'show-other-room-types' |
  'show-recommendations' |
  'select-check-in'

interface CalendarDayClickActionOptions {
  isCheckIn?: boolean
  isInvalidBundleDate: boolean
  isMinimumStayCheckoutDisabled: boolean
  isMaximumStayDisabled: boolean
  isFixedLengthCombinationUnavailable: boolean
  canSelectCheckOut: boolean
  canSelectEarlierCheckIn: boolean
  isFixedFlashLengthOfStayEnabled: boolean
  hasSelectedCheckIn: boolean
  available: boolean
  canSwitchHotel: boolean
  inOtherRoomTypes: boolean
  showRecommendations: boolean
}

function getCalendarDayClickAction(options: CalendarDayClickActionOptions): CalendarDayClickAction {
  if (options.isCheckIn) return 'none'
  if (options.isInvalidBundleDate) return 'invalid-bundle-date'
  if (options.isMinimumStayCheckoutDisabled) return 'minimum-stay-disabled'
  if (options.isMaximumStayDisabled) return 'maximum-stay-disabled'
  if (options.isFixedLengthCombinationUnavailable) return 'none'
  if (options.canSelectCheckOut) return 'select-check-out'
  if (options.canSelectEarlierCheckIn) return 'select-earlier-check-in'
  if (options.isFixedFlashLengthOfStayEnabled && !options.hasSelectedCheckIn && options.available) return 'select-fixed-flash-check-in'
  if (options.canSwitchHotel) return 'switch-to-hotel'
  if (options.inOtherRoomTypes) return 'show-other-room-types'
  if (options.showRecommendations) return 'show-recommendations'

  return 'select-check-in'
}

interface Props {
  isCheckIn?: boolean
  isCheckOut?: boolean
  canHoverCheckOut?: boolean
  hasHoveredCheckOut?: boolean
  isHoveredCheckOut?: boolean
  onHoveredCheckOutChange?: (checkOut?: string) => void
  selectedCheckIn?: string
  selectedCheckOut?: string
  isFixedFlashLengthOfStayEnabled?: boolean
  minimumDuration?: number
  isFixedLengthCombinationUnavailable?: boolean
  isMinimumStayCheckoutDisabled?: boolean
  isMaximumStayDisabled?: boolean
  checkInMinimumStayTooltipOpen?: boolean
  checkInMinimumStayTooltipDescription?: string
  minimumStayTooltipOpen?: boolean
  minimumStayTooltipDescription?: string
  maximumStayTooltipOpen?: boolean
  maximumStayTooltipDescription?: string
  checkoutCalendarDay?: App.CalendarDay
  checkInCalendarDay?: App.CalendarDay
  duration: number
  day: App.CalendarDay
  hasOtherAvailability: boolean
  otherRoomsFromPrice?: OtherRoomsFromPrice
  style?: React.CSSProperties
  isRoomSelected?: boolean
  className?: string
  currentOfferId?: string
  bundleOfferId?: string
  flights?: boolean
  isBetweenDay?: boolean
  /**
   * Show this cell's price. Defaults to true.
   * Range days other than check-in pass false.
   * @default true
   */
  isPriceVisible?: boolean
  isComplementaryBundleCell?: boolean
  isComplementaryCheckIn?: boolean
  isComplementaryCheckOut?: boolean
  complementaryPropertyName?: string
  isIllegalBundleSelection?: boolean
  checkIn: string
  flightsOnly?: boolean
  enableCalendarRecommendations: boolean
  hasPromo?: boolean
  isTacticalPromotion?: boolean
  onClick?: (day: App.CalendarDay) => void
  onMinimumStayDisabledClick?: (day: App.CalendarDay) => void
  onMaximumStayDisabledClick?: (day: App.CalendarDay) => void
  cheapestDay?: App.CalendarCheapestDay
  onInvalidBundleDateClick: () => void
  showUrgencyLabels?: boolean
  soldOutText?: string
  displayMemberPrice: boolean
  userPoints?: number
  loyaltyProductType?: App.LoyaltyProductType
  villasDiscounts?: Array<App.Discount>
  hideChildPrices?: boolean
  markCheapestPrice: boolean
  tier: App.LoyaltyTier
  canLoyaltyUpgrade?: boolean
}

function OfferCalendarDayCell(props: Props) {
  const {
    duration,
    day,
    hasOtherAvailability,
    otherRoomsFromPrice,
    isCheckIn,
    isCheckOut,
    canHoverCheckOut = false,
    hasHoveredCheckOut = false,
    isHoveredCheckOut = false,
    onHoveredCheckOutChange,
    selectedCheckIn,
    selectedCheckOut,
    isFixedFlashLengthOfStayEnabled = false,
    minimumDuration,
    isFixedLengthCombinationUnavailable = false,
    isMinimumStayCheckoutDisabled = false,
    isMaximumStayDisabled = false,
    checkInMinimumStayTooltipOpen = false,
    checkInMinimumStayTooltipDescription,
    minimumStayTooltipOpen = false,
    minimumStayTooltipDescription,
    maximumStayTooltipOpen = false,
    maximumStayTooltipDescription,
    checkoutCalendarDay,
    checkInCalendarDay,
    style,
    isRoomSelected,
    className,
    flights,
    isBetweenDay,
    isPriceVisible = true,
    flightsOnly,
    enableCalendarRecommendations,
    currentOfferId,
    bundleOfferId,
    isComplementaryBundleCell = false,
    isComplementaryCheckIn = false,
    isComplementaryCheckOut = false,
    isIllegalBundleSelection = false,
    complementaryPropertyName,
    onClick,
    hasPromo = false,
    isTacticalPromotion = false,
    onMinimumStayDisabledClick,
    onMaximumStayDisabledClick,
    cheapestDay,
    onInvalidBundleDateClick,
    showUrgencyLabels,
    soldOutText,
    displayMemberPrice,
    userPoints = 0,
    loyaltyProductType,
    villasDiscounts = EmptyArray,
    hideChildPrices,
    markCheapestPrice,
    tier,
    canLoyaltyUpgrade,
  } = props

  const dispatch = useOfferPageDispatch()
  const { open: openRecommendations } = useCalendarRecommendationsView()
  const intl = useIntl()
  const { enabled: displayPointsPrice } = useContext(CalendarBurnContext)
  const defaultSoldOutText = intl.formatMessage({
    id: 'BVklQLY1YNX3jLdcv-wq',
    defaultMessage: 'Sold out',
    description: 'Default sold out text for calendar day cell in the calendar picker',
  })
  const allRoomsSoldOutText = intl.formatMessage({
    id: 'xsHOJDUO1r2747LFoo0E',
    defaultMessage: 'All rooms sold out.',
    description: 'Text shown when a month is sold out across every room type while a room filter is applied in calendar month picker',
  })
  const resolvedSoldOutText = soldOutText ?? (isRoomSelected && enableCalendarRecommendations ? allRoomsSoldOutText : defaultSoldOutText)
  const showOtherRoomsPrice = !!otherRoomsFromPrice && !hideChildPrices && !soldOutText && isPriceVisible

  const checkInLabel = intl.formatMessage({
    id: 'KlDtstL1CZyXebdeOuEL',
    defaultMessage: 'Check in',
    description: 'Label shown on calendar day cell when it is the check-in date',
  })
  const checkOutLabel = intl.formatMessage({
    id: '-zhAvUGisj99DI5paFf0',
    defaultMessage: 'Check out',
    description: 'Label shown on calendar day cell when it is the check-out date',
  })
  const selectCheckInLabel = intl.formatMessage({
    id: 'ct1-O-2qIAqDAJeFKvsH',
    defaultMessage: 'Select check-in date',
    description: 'Label to select check-in date when child prices are hidden in calendar day cell',
  })
  const viewAvailabilityLabel = intl.formatMessage({
    id: 'DXxQaKAck_ansKYI6e4E',
    defaultMessage: 'View availability',
    description: 'Label to view availability when child prices are hidden and check-in is selected in calendar day cell',
  })

  const isAfterSelectedCheckIn = !!selectedCheckIn && moment(day.checkIn).isAfter(selectedCheckIn, 'day')
  const isBeforeSelectedCheckIn = !!selectedCheckIn && moment(day.checkIn).isBefore(selectedCheckIn, 'day')
  const fixedFlashSelectionMode = getFixedFlashCalendarSelectionMode({
    isFixedFlashLengthOfStayEnabled,
    selectedCheckIn,
    selectedCheckOut,
    bundleOfferId,
  })
  const isFixedFlashSelectedRangeMode = fixedFlashSelectionMode === 'selected-range'
  const isFixedFlashCheckoutSelectionMode = fixedFlashSelectionMode === 'select-check-out'
  const isFixedFlashRangePreviewMode = isFixedFlashSelectedRangeMode || (isFixedFlashCheckoutSelectionMode && hasHoveredCheckOut)
  const effectiveCheckoutCalendarDay = isFixedFlashSelectedRangeMode && !isCheckOut && !isCheckIn ? undefined : checkoutCalendarDay
  const effectiveCheckInCalendarDay = isFixedFlashSelectedRangeMode ? undefined : checkInCalendarDay
  const isFromPrice = !isRoomSelected
  const { pricingDay, displayDuration } = getFixedFlashCalendarDayPricingState({
    day,
    fallbackDuration: duration,
    isBeforeSelectedCheckIn,
    selectedCheckOut,
    checkoutCalendarDay: effectiveCheckoutCalendarDay,
    checkInCalendarDay: effectiveCheckInCalendarDay,
  })
  const nightCountLabel = intl.formatMessage(
    {
      id: 'N1zTqGnQ1FrENP3iY4y8',
      defaultMessage: '{nights, plural, one {# night} other {# nights}}',
      description: 'Night count label shown inside offer calendar day cell pricing',
    },
    { nights: displayDuration },
  )
  const flightPriceAvailable = !!pricingDay.pricingWithFlights?.price

  const hasFlightsInPrice = !!(flights && flightPriceAvailable)
  const showFrom = !!(isFromPrice || (flights && !flightPriceAvailable))
  const showPlusFlights = !!(flights && !flightPriceAvailable)
  const basePrice = (displayMemberPrice && pricingDay.memberPricing) ?
      (hasFlightsInPrice ? pricingDay.memberPricingWithFlights!.price : pricingDay.memberPricing.price) :
      (hasFlightsInPrice ? pricingDay.pricingWithFlights!.price : pricingDay.pricing.price)

  const hotelOnlyPrice = (displayMemberPrice && pricingDay.memberPricing) ? pricingDay.memberPricing.price : pricingDay.pricing.price
  const flightCost = hasFlightsInPrice ? Math.max(0, basePrice - hotelOnlyPrice) : 0
  const price = useCalendarBurnDiscountedPrice({
    total: basePrice,
    hotelPrice: hotelOnlyPrice,
    flightPrice: flightCost,
    loyaltyProductType,
    userPoints,
    enabled: !!(displayPointsPrice && userPoints > 0),
  })
  const baseAvailable = !!effectiveCheckoutCalendarDay || !!effectiveCheckInCalendarDay || (day.hotelAvailable && (!flights || day.flightsAvailable) && (flightsOnly ? day.flightsAvailable : true))
  const hasPrice = basePrice > 0
  const canSelectSelectedRangeCheckIn = isFixedFlashSelectedRangeMode && day.hotelAvailable && (!flights || day.flightsAvailable) && (flightsOnly ? day.flightsAvailable : true) && hasPrice
  const available = isFixedFlashSelectedRangeMode ? baseAvailable && hasPrice : baseAvailable
  const canSwitchToHotel = day.hotelAvailable && flights && !flightsOnly
  const colour = (isCheckIn || isCheckOut || isHoveredCheckOut) ? 'normal-contrast' : undefined
  const showLuxPlus = displayMemberPrice && pricingDay.memberPricing && !(isCheckIn || isCheckOut || isHoveredCheckOut)

  const colorString = useMemo<ComponentProps<typeof BodyText>['colour']>(() => {
    if (hasPromo) {
      return 'highlight-primary-dark'
    } else if (showLuxPlus) {
      return 'lux-plus'
    } else {
      return markCheapestPrice && !colour && day.cheapest ? 'highlight-primary-dark' : colour
    }
  },
  [hasPromo, showLuxPlus, colour, day.cheapest, markCheapestPrice])

  const availability = useMemo(() => {
    const result = {
      inOtherRoomTypes: false,
      showRecommendations: false,
      available: false,
      soldOut: false,
      canSwitchHotel: false,
    }
    if (available) {
      result.available = true
    } else if (!available) {
      if (canSwitchToHotel) {
        result.canSwitchHotel = true
      } else if (hasOtherAvailability) {
        result.inOtherRoomTypes = true
      } else {
        if (enableCalendarRecommendations) {
          result.showRecommendations = true
        } else {
          result.soldOut = true
        }
      }
    }
    return result
  }, [available, canSwitchToHotel, hasOtherAvailability, enableCalendarRecommendations])

  const isInvalidBundleDate = !isComplementaryCheckOut && isIllegalBundleSelection
  const effectiveIsFixedLengthCombinationUnavailable = isFixedFlashSelectedRangeMode ? false : isFixedLengthCombinationUnavailable
  const effectiveIsMinimumStayCheckoutDisabled = isFixedFlashSelectedRangeMode ? false : isMinimumStayCheckoutDisabled
  const effectiveIsMaximumStayDisabled = isFixedFlashSelectedRangeMode ? false : isMaximumStayDisabled
  const canSelectCheckOut = isFixedFlashCheckoutSelectionMode && isAfterSelectedCheckIn && !!checkoutCalendarDay
  const canSelectEarlierCheckIn = isFixedFlashCheckoutSelectionMode && isBeforeSelectedCheckIn && availability.available
  const shouldHideSelectedCheckInPrice = fixedFlashSelectionMode !== 'inactive' && !isFixedFlashSelectedRangeMode && !!isCheckIn
  const shouldShowSelectedCheckOutPrice = isFixedFlashSelectedRangeMode && !!checkoutCalendarDay && !!isCheckOut

  const display = useMemo(() => {
    const cellSelection = {
      isBetweenDay,
      isCheckOut,
      isCheckIn,
      isNone: !isBetweenDay && !isCheckIn && !isCheckOut,
    }
    const showNoAvailability = effectiveIsFixedLengthCombinationUnavailable &&
      !effectiveIsMinimumStayCheckoutDisabled &&
      !effectiveIsMaximumStayDisabled
    const shouldShowFixedFlashBetweenDayPrice = isFixedFlashRangePreviewMode &&
      cellSelection.isBetweenDay &&
      (!!effectiveCheckoutCalendarDay || availability.available)
    const shouldShowFixedFlashBetweenDaySoldOut = isFixedFlashRangePreviewMode &&
      cellSelection.isBetweenDay &&
      !shouldShowFixedFlashBetweenDayPrice &&
      (availability.showRecommendations || availability.soldOut)
    const shouldShowStayDisabled = effectiveIsMinimumStayCheckoutDisabled || effectiveIsMaximumStayDisabled

    return {
      cellSelection,
      shouldShowBetweenDay: cellSelection.isBetweenDay && !shouldShowStayDisabled,
      shouldDisableButton: (!effectiveIsMinimumStayCheckoutDisabled && !effectiveIsMaximumStayDisabled && effectiveIsFixedLengthCombinationUnavailable) ||
        (!shouldShowStayDisabled && !shouldShowFixedFlashBetweenDaySoldOut && !canSelectCheckOut && (
          availability.soldOut || (availability.showRecommendations && !cellSelection.isNone && !isFixedFlashSelectedRangeMode)
        )),
      isUnselectable: isInvalidBundleDate || effectiveIsFixedLengthCombinationUnavailable || effectiveIsMinimumStayCheckoutDisabled || effectiveIsMaximumStayDisabled,
      shouldHideMainInformation: shouldShowStayDisabled || (cellSelection.isBetweenDay && !shouldShowFixedFlashBetweenDayPrice && !shouldShowFixedFlashBetweenDaySoldOut) || (cellSelection.isCheckOut && !shouldShowSelectedCheckOutPrice) || (isComplementaryBundleCell && !isComplementaryCheckOut),
      showNoAvailability,
      showSwitchToViewHotel: availability.canSwitchHotel,
      showAvailableInOtherRoomTypes: availability.inOtherRoomTypes,
      showSoldOut: !showNoAvailability && !shouldShowStayDisabled && (availability.showRecommendations || availability.soldOut),
      showRecommendationLink: !showNoAvailability && !shouldShowStayDisabled && availability.showRecommendations && (cellSelection.isNone || shouldShowFixedFlashBetweenDaySoldOut) && !bundleOfferId,
      showSoldOutBackground: !showNoAvailability && !shouldShowStayDisabled && availability.showRecommendations && cellSelection.isNone,
      showPrices: isPriceVisible && !effectiveIsFixedLengthCombinationUnavailable && !effectiveIsMinimumStayCheckoutDisabled && !effectiveIsMaximumStayDisabled && !shouldHideSelectedCheckInPrice && (
        shouldShowSelectedCheckOutPrice || (effectiveCheckoutCalendarDay ? true : availability.available && (!flightsOnly || (flightsOnly && day.flightCacheActive)))
      ),
      showUrgencyLabel: showUrgencyLabels && isRoomSelected && !isBetweenDay && !isCheckIn && !isCheckOut && !shouldShowStayDisabled,
      isBundleSelection: !!bundleOfferId,
      complementaryCell: isComplementaryBundleCell,
      complementaryCheckIn: isComplementaryCheckIn,
      complementaryCheckOut: isComplementaryCheckOut,
      shouldWhitenDay: isComplementaryCheckIn || isComplementaryCheckOut,
      complementaryCheckInIntersect: isComplementaryCheckIn && isCheckOut,
      complementaryCheckOutIntersect: isComplementaryCheckOut && isCheckIn,
      inversePallete: isComplementaryBundleCell && !(isComplementaryCheckIn || isComplementaryCheckOut),
      showPropertyName: isComplementaryCheckIn,
    }
  }, [
    isBetweenDay,
    isCheckOut,
    isCheckIn,
    availability.soldOut,
    availability.showRecommendations,
    availability.canSwitchHotel,
    availability.inOtherRoomTypes,
    availability.available,
    isInvalidBundleDate,
    isComplementaryBundleCell,
    isComplementaryCheckOut,
    bundleOfferId,
    flightsOnly,
    day.flightCacheActive,
    showUrgencyLabels,
    isRoomSelected,
    isComplementaryCheckIn,
    canSelectCheckOut,
    effectiveCheckoutCalendarDay,
    effectiveIsFixedLengthCombinationUnavailable,
    effectiveIsMinimumStayCheckoutDisabled,
    effectiveIsMaximumStayDisabled,
    shouldHideSelectedCheckInPrice,
    shouldShowSelectedCheckOutPrice,
    isPriceVisible,
    isFixedFlashSelectedRangeMode,
    isFixedFlashRangePreviewMode,
  ])

  const showRoomsLabel = !display.showSoldOut && display.showUrgencyLabel && !(display.complementaryCell && !display.complementaryCheckOut) && !isHoveredCheckOut
  const showSelectedCheckOutLabel = display.cellSelection.isCheckOut && (
    !isFixedFlashCheckoutSelectionMode || !hasHoveredCheckOut || isHoveredCheckOut
  )
  const showCheckOutLabel = showSelectedCheckOutLabel || isHoveredCheckOut
  const showCompactPriorDatePrice = isFixedFlashCheckoutSelectionMode &&
    isBeforeSelectedCheckIn &&
    display.showPrices &&
    !hideChildPrices &&
    !bundleOfferId

  const onCellClick = useCallback(() => {
    const dispatchDates = (checkIn?: string, checkOut?: string) => {
      if (isFixedFlashLengthOfStayEnabled && minimumDuration !== undefined) {
        dispatch({ type: OfferPageActions.setCalendarDates, checkIn, checkOut, minimumDuration })
        return
      }

      dispatch({ type: OfferPageActions.setDates, checkIn, checkOut })
    }

    if (isFixedFlashSelectedRangeMode) {
      if (isCheckOut) {
        dispatchDates(undefined, undefined)
        dispatch({ type: OfferPageActions.setLastInteractedCalendarDay, lastInteractedCalendarDay: undefined })
        onClick?.(day)
        return
      }
      if (availability.showRecommendations && !isCheckIn) {
        openRecommendations({ checkIn: day.checkIn })
        onClick?.(day)
        return
      }
      if (canSelectSelectedRangeCheckIn && !isCheckIn) {
        dispatchDates(day.checkIn, undefined)
        dispatch({ type: OfferPageActions.setLastInteractedCalendarDay, lastInteractedCalendarDay: day })
        onClick?.(day)
        return
      }
      return
    }

    const clickAction = getCalendarDayClickAction({
      isCheckIn,
      isInvalidBundleDate,
      isMinimumStayCheckoutDisabled: effectiveIsMinimumStayCheckoutDisabled,
      isMaximumStayDisabled: effectiveIsMaximumStayDisabled,
      isFixedLengthCombinationUnavailable: effectiveIsFixedLengthCombinationUnavailable,
      canSelectCheckOut,
      canSelectEarlierCheckIn,
      isFixedFlashLengthOfStayEnabled,
      hasSelectedCheckIn: !!selectedCheckIn,
      available: availability.available,
      canSwitchHotel: availability.canSwitchHotel,
      inOtherRoomTypes: availability.inOtherRoomTypes,
      showRecommendations: availability.showRecommendations,
    })

    switch (clickAction) {
      case 'none':
        return
      case 'invalid-bundle-date':
        onInvalidBundleDateClick()
        return
      case 'minimum-stay-disabled':
        onMinimumStayDisabledClick?.(day)
        return
      case 'maximum-stay-disabled':
        onMaximumStayDisabledClick?.(day)
        return
      case 'select-check-out':
        dispatchDates(selectedCheckIn, day.checkIn)
        break
      case 'select-earlier-check-in':
      case 'select-fixed-flash-check-in':
        dispatchDates(day.checkIn, undefined)
        dispatch({ type: OfferPageActions.setLastInteractedCalendarDay, lastInteractedCalendarDay: day })
        break
      case 'switch-to-hotel':
        dispatch({ type: OfferPageActions.setFlights, flights: false })
        break
      case 'show-other-room-types':
        dispatch({ type: OfferPageActions.toggleSubFilter, filter: 'room' })
        dispatch({ type: OfferPageActions.setCheckInDate, checkIn: day.checkIn, offerId: currentOfferId })
        dispatch({ type: OfferPageActions.setLastInteractedCalendarDay, lastInteractedCalendarDay: day })
        break
      case 'show-recommendations':
        openRecommendations({ checkIn: day.checkIn })
        break
      case 'select-check-in':
        dispatch({ type: OfferPageActions.setCheckInDate, checkIn: day.checkIn, offerId: currentOfferId })
        dispatch({ type: OfferPageActions.setLastInteractedCalendarDay, lastInteractedCalendarDay: day })
        break
    }

    onClick?.(day)
  }, [
    isCheckIn,
    isCheckOut,
    isInvalidBundleDate,
    availability.canSwitchHotel,
    availability.inOtherRoomTypes,
    availability.showRecommendations,
    availability.available,
    onClick,
    day,
    onInvalidBundleDateClick,
    dispatch,
    currentOfferId,
    canSelectCheckOut,
    canSelectEarlierCheckIn,
    canSelectSelectedRangeCheckIn,
    isFixedFlashSelectedRangeMode,
    effectiveIsFixedLengthCombinationUnavailable,
    effectiveIsMinimumStayCheckoutDisabled,
    effectiveIsMaximumStayDisabled,
    isFixedFlashLengthOfStayEnabled,
    onMinimumStayDisabledClick,
    onMaximumStayDisabledClick,
    selectedCheckIn,
    minimumDuration,
    openRecommendations,
  ])

  const onMouseEnter = useCallback(() => {
    if (canHoverCheckOut) {
      onHoveredCheckOutChange?.(day.checkIn)
    }
  }, [canHoverCheckOut, day.checkIn, onHoveredCheckOutChange])

  const onMouseLeave = useCallback(() => {
    if (isHoveredCheckOut) {
      onHoveredCheckOutChange?.()
    }
  }, [isHoveredCheckOut, onHoveredCheckOutChange])

  let bundleAdditionalAmount = 0
  if (cheapestDay?.pricing) {
    const diff = day.pricing.price - cheapestDay.pricing.price
    if (diff >= 1) {
      bundleAdditionalAmount = diff
    }
  } else if (day.surcharge > 0) {
    bundleAdditionalAmount = day.surcharge
  }

  let FireIcon: React.ReactElement
  if (isLPCTacticalEnabled()) {
    FireIcon = <SolidFireIcon size="XS" colour={!colour ? 'urgency' : undefined} />
  } else {
    FireIcon = <LineFireIcon size="XS" colour={!colour ? 'highlight-primary-dark' : undefined} />
  }

  const isDateInVillasDiscountPeriod = villasDiscounts &&
    villasDiscounts.length > 0 &&
    isDateWithinAnyDiscountPeriod(villasDiscounts, moment(day.checkIn), displayDuration ?? duration)

  const dayIcon = useMemo(() => {
    if (day.hasFlightDeal && flights) {
      return <SolidFireIcon size="XS" colour="urgency" />
    } else if ((!!day.loyalty.availableUpgradeTargetsUniqueKeys.room || !!day.loyalty.availableUpgradeTargetsUniqueKeys.package) && canLoyaltyUpgrade) {
      return <SolidCircleIcon size="XS" colour={`tier-${tier}`} />
    }
  }, [day, flights, tier, canLoyaltyUpgrade])

  return (
    <CellWrapper
      style={style}
      data-testid={clsx({
        'available-day-cell': availability.available,
        'alternate-day-cell': availability.showRecommendations,
      })}
      className={clsx(className, getThemeClassName({ mobile: display.inversePallete && !display.isBundleSelection ? 'inverse' : 'default' }), {
        unselectable: display.isUnselectable,
        'other-availability': display.showAvailableInOtherRoomTypes,
        'check-in': display.cellSelection.isCheckIn,
        'check-out': display.cellSelection.isCheckOut || isHoveredCheckOut,
        'between-day': display.shouldShowBetweenDay,
        'minimum-stay': effectiveIsMinimumStayCheckoutDisabled,
        'compact-prior-date': showCompactPriorDatePrice,
        'sold-out-background': display.showSoldOutBackground,
        'is-complementary': display.complementaryCell,
        'complementary-check-in': display.complementaryCheckIn,
        'complementary-check-out': display.complementaryCheckOut,
        'is-bundle': display.isBundleSelection,
      })}
      disabled={display.shouldDisableButton}
      aria-disabled={display.isUnselectable}
      onClick={onCellClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <HeaderGroup
        fullWidth
        direction="horizontal"
        verticalAlign="space-evenly"
        horizontalAlign="space-between"
        data-testid={clsx({ [`d-${day.checkIn}`]: availability.available })}
      >
        <Group direction="horizontal" gap={4} verticalAlign="center">
          <BodyText
            colour={display.shouldWhitenDay ? 'dark-contrast' : colour}
            variant="medium"
          >
            {day.day}
          </BodyText>
          {dayIcon}
        </Group>
        <Group direction="horizontal" verticalAlign="center" gap={4}>
          {(display.cellSelection.isCheckIn || showCheckOutLabel) && <BodyText colour="normal-contrast" as="span" variant="small" weight="bold">
            {display.cellSelection.isCheckIn && checkInLabel}
            {showCheckOutLabel && checkOutLabel}
          </BodyText>}
          {showRoomsLabel && <RoomsRemainingLabel roomCount={day.availableRooms} />}
          {hasPromo && !display.cellSelection.isBetweenDay && !effectiveIsMinimumStayCheckoutDisabled && !effectiveIsMaximumStayDisabled && <SolidUsdCircleIcon size="XS" colour={!colour ? 'highlight-primary-dark' : undefined} />}
          {!hasPromo && display.cellSelection.isNone && isTacticalPromotion && !effectiveIsMinimumStayCheckoutDisabled && !effectiveIsMaximumStayDisabled && FireIcon}
          {isDateInVillasDiscountPeriod && !display.showSoldOut && !effectiveIsMinimumStayCheckoutDisabled && !effectiveIsMaximumStayDisabled && FireIcon}
        </Group>
      </HeaderGroup>

      {display.shouldHideMainInformation && display.showPropertyName && complementaryPropertyName &&
        <PropertyNameContainer>
          <BodyText colour="dark-contrast" lineClamp={2} underline variant="small" weight="bold">{complementaryPropertyName}</BodyText>
        </PropertyNameContainer>}

      {effectiveIsMinimumStayCheckoutDisabled && minimumStayTooltipDescription && <Caption variant="large" colour="light-foreground-alt">
        {minimumStayTooltipDescription}
      </Caption>}

      {!display.shouldHideMainInformation && <PriorDatePriceSlot className={clsx({ 'compact-prior-date': showCompactPriorDatePrice })}>
        {showCompactPriorDatePrice && <CompactPriorDatePrice
          className="compact-prior-date-price"
          data-testid="compact-prior-date-price"
        >
          <CompactPriorDatePricePlaceholder aria-hidden="true" />
          <CompactPriorDatePriceRow>
            <FormattedMessage
              id="in3PB1bh8827qXtZE8KT"
              defaultMessage="<from>From</from> <price>{priceValue}</price>"
              description="Compact price shown for alternative check-in dates before the selected check-in date"
              values={{
                priceValue: <FormatCurrency value={price} />,
                from: (chunks: React.ReactNode) => <Caption colour={colour ?? 'light-foreground-alt'} as="span" variant="medium">{chunks}</Caption>,
                price: (chunks: React.ReactNode) => (
                  <CompactPriorDatePriceValue>
                    <BodyText colour={colorString} as="span" variant="medium" weight="bold">{chunks}</BodyText>
                  </CompactPriorDatePriceValue>
                ),
              }}
            />
          </CompactPriorDatePriceRow>
        </CompactPriorDatePrice>}

        <Main
          data-testid="d-main"
          className={clsx({
            'align-items-end': display.isBundleSelection,
            'full-day-information': showCompactPriorDatePrice,
          })}
        >
          {display.showPrices && <>
            {bundleOfferId && (
              <BundleAndSaveAdditionalAmount data-testid="day-price">
                <BodyText colour={colour} variant="small" weight="normal">
                  <FormattedMessage
                    id="9AAzlOGv2NQzQE-fRseS"
                    defaultMessage="{nights, plural, one {# night} other {# nights}}"
                    description="Night count display in the bundle calendar day cell"
                    values={{ nights: displayDuration }}
                  />
                </BodyText>
                {bundleAdditionalAmount > 0 && (
                  <BodyText colour={colour} variant="small" weight="normal">
                    <FormattedMessage
                      id="B62fmHxlIQVUaZmkdOEx"
                      defaultMessage="from +{price}*"
                      description="Additional surcharge price in bundle calendar day cell"
                      values={{ price: <FormatCurrency value={bundleAdditionalAmount} /> }}
                    />
                  </BodyText>
                )}
              </BundleAndSaveAdditionalAmount>
            )}
            {!bundleOfferId && (<>
              {hideChildPrices && <Caption variant="large" colour={colour ?? 'light-foreground-alt'} underline>
                {display.cellSelection.isNone && selectCheckInLabel}
                {display.cellSelection.isCheckIn && viewAvailabilityLabel}
              </Caption>}
              {!hideChildPrices && <>
                {hasFlightsInPrice && showFrom && <FormattedMessage
                  id="mq4-EQb61eXPL1YTjRR0"
                  defaultMessage="<description>{nightCountLabel} + flights from</description> <priceWrap>{priceValue}*</priceWrap>"
                  description="Calendar day cell: nights + flights from price with asterisk when flights are included"
                  values={{
                    nightCountLabel,
                    priceValue: <FormatCurrency value={price} />,
                    description: (chunks) => <Caption colour={colour ?? 'light-foreground-alt'} variant="medium">{chunks}</Caption>,
                    priceWrap: (chunks) => <div data-testid="day-price"><BodyText colour={colorString} as="span" variant="medium" weight="bold">{chunks}</BodyText></div>,
                  }}
                />}
                {hasFlightsInPrice && !showFrom && <FormattedMessage
                  id="Pf3N_Ra-JlxNBq_V1nre"
                  defaultMessage="<description>{nightCountLabel} + flights</description> <priceWrap>{priceValue}*</priceWrap>"
                  description="Calendar day cell: nights + flights price with asterisk when flights are included"
                  values={{
                    nightCountLabel,
                    priceValue: <FormatCurrency value={price} />,
                    description: (chunks) => <Caption colour={colour ?? 'light-foreground-alt'} variant="medium">{chunks}</Caption>,
                    priceWrap: (chunks) => <div data-testid="day-price"><BodyText colour={colorString} as="span" variant="medium" weight="bold">{chunks}</BodyText></div>,
                  }}
                />}
                {showPlusFlights && <FormattedMessage
                  id="LS5dpFApb08_HWRASuMG"
                  defaultMessage="<description>{nightCountLabel} from</description> <priceWrap>{priceValue}* <plusFlights>+ flights</plusFlights></priceWrap>"
                  description="Calendar day cell: nights from hotel price with asterisk and plus flights suffix"
                  values={{
                    nightCountLabel,
                    priceValue: <FormatCurrency value={price} />,
                    description: (chunks) => <Caption colour={colour ?? 'light-foreground-alt'} variant="medium">{chunks}</Caption>,
                    priceWrap: (chunks) => <div data-testid="day-price"><BodyText colour={colorString} as="span" variant="medium" weight="bold">{chunks}</BodyText></div>,
                    plusFlights: (chunks) => <Caption colour={colour} as="span" variant="medium">{chunks}</Caption>,
                  }}
                />}
                {!flights && showFrom && <FormattedMessage
                  id="XC2hcTt-1eb5kvUTEAcr"
                  defaultMessage="<description>{nightCountLabel} from</description> <priceWrap>{priceValue}</priceWrap>"
                  description="Calendar day cell: nights from hotel price without flights in the calendar picker"
                  values={{
                    nightCountLabel,
                    priceValue: <FormatCurrency value={price} />,
                    description: (chunks) => <Caption colour={colour ?? 'light-foreground-alt'} variant="medium">{chunks}</Caption>,
                    priceWrap: (chunks) => <div data-testid="day-price"><BodyText colour={colorString} as="span" variant="medium" weight="bold">{chunks}</BodyText></div>,
                  }}
                />}
                {!flights && !showFrom && <FormattedMessage
                  id="WZVRnXon1P8otJgRpD-6"
                  defaultMessage="<description>{nightCountLabel}</description> <priceWrap>{priceValue}</priceWrap>"
                  description="Calendar day cell: nights and hotel price without flights in the calendar picker"
                  values={{
                    nightCountLabel,
                    priceValue: <FormatCurrency value={price} />,
                    description: (chunks) => <Caption colour={colour ?? 'light-foreground-alt'} variant="medium">{chunks}</Caption>,
                    priceWrap: (chunks) => <div data-testid="day-price"><BodyText colour={colorString} as="span" variant="medium" weight="bold">{chunks}</BodyText></div>,
                  }}
                />}
                {displayPointsPrice && userPoints > 0 && price !== basePrice && (
                  <Caption variant="medium" colour="light-foreground-alt"><s><FormatCurrency value={basePrice} /></s></Caption>
                )}
              </>}
            </>)}
          </>}

          {display.showSwitchToViewHotel && <>
            <Caption variant="large" colour={colour ?? 'light-foreground-alt'}>
              {intl.formatMessage({
                id: '9YXkp23karhgddGJOL2s',
                defaultMessage: 'Flights not available.',
                description: 'Message shown when flights are not available for calendar day cell',
              })}
            </Caption>
            <TextLink size="small" weight="regular">
              {intl.formatMessage({
                id: 'w3miWxKIL3wpQbfJ33Gp',
                defaultMessage: 'View hotel prices',
                description: 'Link text to view hotel-only prices when flights are not available',
              })}
            </TextLink>
          </>}

          {display.showSoldOut && <Caption variant="large" colour={colour ?? 'light-foreground-alt'}>{resolvedSoldOutText}</Caption>}
          {display.showRecommendationLink && <TextLink size="small" weight="regular">
            {intl.formatMessage({
              id: 'os5Oyb_wcznG2qIjMcHY',
              defaultMessage: 'View similar offers',
              description: 'Link text to view similar offers when a month is sold out in calendar month picker',
            })}
          </TextLink>}

          {display.showNoAvailability && <Caption variant="large" colour={colour ?? 'light-foreground-alt'}>
            {intl.formatMessage({
              id: '_kKl7G4ZGu2A6cmjqKZx',
              defaultMessage: 'No availability',
              description: 'Message shown when a fixed length of stay check-in and checkout date combination is unavailable',
            })}
          </Caption>}

          {display.showAvailableInOtherRoomTypes && (<>
            {otherRoomsFromPrice && showOtherRoomsPrice && <Caption variant="medium" colour="light-foreground-alt" data-testid="day-cell-other-rooms-price">
              <FormattedMessage
                id="JmOTHzmpwFVNx9ggCjOW"
                defaultMessage="<b>Sold out.</b> Other rooms available from {price}"
                description="Text shown when the filtered room is sold out for a month, with the cheapest price available in other room types"
                values={{
                  b: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
                  price: <div>
                    <BodyText as="span" variant="medium" weight="bold" colour={otherRoomsFromPrice.isLuxPlusPrice ? 'lux-plus' : undefined}>
                      <FormatCurrency value={otherRoomsFromPrice.total} />
                    </BodyText>
                  </div>,
                }}
              />
            </Caption>}
            {!showOtherRoomsPrice && <Caption variant="large" colour="light-foreground-alt">
              {soldOutText ?? intl.formatMessage({
                id: '0tk2sBBchrOmz8eH1vvj',
                defaultMessage: 'Sold out.',
                description: 'Text shown when the filtered room is sold out for a month but other room types are available, without a price',
              })}
            </Caption>}
            <TextLink size="small" weight="regular">
              {intl.formatMessage({
                id: 'b0xnOuwndheerj7ia8ea',
                defaultMessage: 'View rooms',
                description: 'Link text shown when the filtered room is sold out but other room types are available in month tile',
              })}
            </TextLink>
          </>)}
        </Main>
      </PriorDatePriceSlot>}
      {isCheckIn && checkInMinimumStayTooltipDescription && checkInMinimumStayTooltipOpen && <CalendarDayTooltip role="tooltip" variant="small">
        {checkInMinimumStayTooltipDescription}
      </CalendarDayTooltip>}
      {effectiveIsMinimumStayCheckoutDisabled && minimumStayTooltipDescription && minimumStayTooltipOpen && <CalendarDayTooltip role="tooltip" variant="small">
        {minimumStayTooltipDescription}
      </CalendarDayTooltip>}
      {effectiveIsMaximumStayDisabled && maximumStayTooltipDescription && maximumStayTooltipOpen && <CalendarDayTooltip role="tooltip" variant="small">
        {maximumStayTooltipDescription}
      </CalendarDayTooltip>}
    </CellWrapper>
  )
}

export default React.memo(OfferCalendarDayCell)
